import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { useSessionTokenHash } from '@/hooks/useSessionTokenHash';
import { supabase } from '@/lib/supabase';
import type { SessionSettings } from '@/types/sessions';

interface UseSessionManagerOptions {
  onTimeout?: () => void;
  onWarning?: (remainingSeconds: number) => void;
  clinicId?: string | null;
  /** When false, skips staff session tracking (e.g. patient portal). */
  enabled?: boolean;
}

interface SessionManagerState {
  isActive: boolean;
  showWarning: boolean;
  remainingSeconds: number;
  settings: SessionSettings | null;
}

/**
 * Core session management hook that handles:
 * - Activity tracking via heartbeat
 * - Inactivity timeout detection
 * - Warning before timeout
 * - Automatic logout on timeout
 * - Session revocation checking
 */
export function useSessionManager(options: UseSessionManagerOptions = {}) {
  const enabled = options.enabled ?? true;
  const { session, signOut } = useAuth();
  const [state, setState] = useState<SessionManagerState>({
    isActive: true,
    showWarning: false,
    remainingSeconds: 0,
    settings: null,
  });

  const lastActivityRef = useRef<number>(Date.now());
  const heartbeatIntervalRef = useRef<number | null>(null);
  const timeoutCheckIntervalRef = useRef<number | null>(null);
  const sessionTokenHash = useSessionTokenHash(session?.access_token);

  // Load session settings
  useEffect(() => {
    if (!enabled || !session) return;

    const loadSettings = async () => {
      try {
        const { data, error } = await supabase.rpc('get_session_settings', {
          p_clinic_id: options.clinicId ?? undefined,
        });

        if (error) throw error;
        setState((prev) => ({ ...prev, settings: data as SessionSettings }));
      } catch (err) {
        console.error('Failed to load session settings:', err);
        // Use sensible defaults if settings can't be loaded
        setState((prev) => ({
          ...prev,
          settings: {
            id: '',
            clinic_id: null,
            inactivity_timeout_minutes: 30,
            warning_before_timeout_minutes: 5,
            absolute_timeout_hours: 12,
            allow_trusted_devices: true,
            trusted_device_duration_days: 30,
            force_logout_on_ip_change: false,
            max_concurrent_sessions: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        }));
      }
    };

    void loadSettings();
  }, [enabled, session, options.clinicId]);

  // Track user activity
  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setState((prev) => ({ ...prev, isActive: true, showWarning: false }));
  }, []);

  // Send heartbeat to server
  const sendHeartbeat = useCallback(async () => {
    if (!sessionTokenHash) return;

    try {
      await supabase.rpc('update_session_activity', {
        p_session_token: sessionTokenHash,
      });
      // Update client-side activity tracker to match server
      lastActivityRef.current = Date.now();
    } catch (err) {
      console.error('Failed to send heartbeat:', err);
    }
  }, [sessionTokenHash]);

  // Check if session should timeout
  const checkTimeout = useCallback(() => {
    if (!state.settings) return;

    const now = Date.now();
    const inactiveMs = now - lastActivityRef.current;
    const inactiveMinutes = inactiveMs / 1000 / 60;

    const timeoutMinutes = state.settings.inactivity_timeout_minutes;
    const warningMinutes = state.settings.warning_before_timeout_minutes;

    // Calculate remaining time
    const remainingMinutes = timeoutMinutes - inactiveMinutes;
    const remainingSeconds = Math.floor(remainingMinutes * 60);

    // If past timeout, logout
    if (remainingMinutes <= 0) {
      setState((prev) => ({ ...prev, isActive: false, showWarning: false }));
      options.onTimeout?.();
      void signOut();
      return;
    }

    // If within warning window, show warning
    if (remainingMinutes <= warningMinutes) {
      setState((prev) => ({
        ...prev,
        showWarning: true,
        remainingSeconds,
      }));
      options.onWarning?.(remainingSeconds);
    } else {
      setState((prev) => ({ ...prev, showWarning: false }));
    }
  }, [state.settings, options, signOut]);

  // Extend session (reset activity timer)
  const extendSession = useCallback(() => {
    recordActivity();
    void sendHeartbeat();
  }, [recordActivity, sendHeartbeat]);

  // Set up activity listeners
  useEffect(() => {
    if (!enabled || !session) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((event) => {
      window.addEventListener(event, recordActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, recordActivity);
      });
    };
  }, [enabled, session, recordActivity]);

  // Set up heartbeat interval (every 60 seconds)
  useEffect(() => {
    if (!enabled || !session || !sessionTokenHash) return;

    // Send initial heartbeat
    void sendHeartbeat();

    heartbeatIntervalRef.current = window.setInterval(sendHeartbeat, 60_000);

    return () => {
      if (heartbeatIntervalRef.current !== null) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [enabled, session, sessionTokenHash, sendHeartbeat]);

  // Set up timeout check interval (every 10 seconds)
  useEffect(() => {
    if (!enabled || !session || !state.settings) return;

    timeoutCheckIntervalRef.current = window.setInterval(checkTimeout, 10_000);

    return () => {
      if (timeoutCheckIntervalRef.current !== null) {
        clearInterval(timeoutCheckIntervalRef.current);
      }
    };
  }, [enabled, session, state.settings, checkTimeout]);

  // Check for session revocation on mount and periodically
  useEffect(() => {
    if (!enabled || !session || !sessionTokenHash) return;

    const checkRevocation = async () => {
      try {
        const { data, error } = await supabase
          .from('user_sessions')
          .select('is_revoked')
          .eq('session_token', sessionTokenHash)
          .single();

        if (error) {
          // Session not found or error - let Supabase handle it
          return;
        }

        if (data?.is_revoked) {
          console.log('Session was revoked, logging out');
          await signOut();
        }
      } catch (err) {
        console.error('Failed to check session revocation:', err);
      }
    };

    void checkRevocation();
    const interval = setInterval(checkRevocation, 30_000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [enabled, session, sessionTokenHash, signOut]);

  return {
    ...state,
    extendSession,
    recordActivity,
  };
}
